import type { OptionType } from '../../index.types';
import { X } from 'lucide-react';
import React from 'react';
import { MultiValueProps } from 'react-select';
import { cn } from '~/lib/utils';
import { AutocompleteAvatar } from '../AutocompleteAvatar';

/**
 * Default chip component for the Autocomplete.
 *
 * This component renders individual chips in the autocomplete dropdown with:
 * - Avatar display (image or fallback initials)
 * - Title text
 * - Remove button for individual chips
 *
 * The component handles two types of contacts:
 * - Regular contacts: Display avatar image or fallback initials
 * - Unsaved contacts: Display a user icon in muted background
 *
 * @param props - MultiValueProps containing the chip data and configuration
 * @returns A React element representing a chip with avatar, title, and optional remove button
 */
export const AutocompleteValue = (
  props: MultiValueProps<OptionType, true>
): React.ReactElement => {
  const { children, data, isDisabled, ...restProps } = props;

  return (
    <div
      className={cn(
        'inline-flex items-center bg-muted text-foreground text-sm font-medium h-6 px-1 rounded-full mr-1',
        restProps.className
      )}
    >
      <AutocompleteAvatar data={data} size="sm" className="-ml-1 mr-2" />

      {data?.title && <span className="mr-1">{data?.title}</span>}

      {!isDisabled && (
        <button
          {...restProps?.removeProps}
          className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-secondary/30 transition-colors"
          type="button"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
};
